<template>
  <div class="submission-success">
    <div class="success-content text-center">

      <!-- Success Icon -->
      <div class="success-icon-container mb-4">
        <div class="success-icon animated bounceIn">
          <i class="fas fa-check-circle"></i>
        </div>
      </div>

      <!-- Success Message -->
      <div class="success-message mb-4">
        <h3 class="text-success">Request Submitted Successfully!</h3>
        <p class="lead text-muted">
          Your property update request has been submitted and is now under review.
        </p>
      </div>

      <!-- Reference Information -->
      <div class="reference-info mb-4">
        <div class="card border-success">
          <div class="card-header bg-success text-white">
            <h6 class="mb-0">
              <i class="fas fa-receipt mr-2"></i>
              Reference Information
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <p class="mb-2">
                  <strong>Reference Number:</strong><br>
                  <span class="text-primary font-weight-bold">{{ submissionData.referenceNumber }}</span>
                </p>
                <p class="mb-2">
                  <strong>Property LR Number:</strong><br>
                  {{ submissionData.lrNumber }}
                </p>
              </div>
              <div class="col-md-6">
                <p class="mb-2">
                  <strong>Submission Date:</strong><br>
                  {{ formatDate(submissionData.submittedAt) }}
                </p>
                <p class="mb-2">
                  <strong>Status:</strong><br>
                  <span class="badge badge-warning">{{ submissionData.status.toUpperCase() }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Update Summary -->
      <div class="update-summary mb-4">
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="fas fa-list mr-2"></i>
              Update Summary
            </h6>
          </div>
          <div class="card-body">
            <div class="update-types">
              <div class="row">
                <div class="col-md-6">
                  <div v-if="submissionData.updateTypes.ownerInfo" class="update-type-item">
                    <i class="fas fa-user text-info mr-2"></i>
                    <span>Owner Information Update</span>
                  </div>
                  <div v-if="submissionData.updateTypes.propertyDetails" class="update-type-item">
                    <i class="fas fa-home text-success mr-2"></i>
                    <span>Property Details Update</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div v-if="submissionData.updateTypes.ownershipTransfer" class="update-type-item">
                    <i class="fas fa-exchange-alt text-warning mr-2"></i>
                    <span>Ownership Transfer</span>
                  </div>
                  <div v-if="submissionData.updateTypes.propertyImprovements" class="update-type-item">
                    <i class="fas fa-tools text-primary mr-2"></i>
                    <span>Property Improvements</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="submissionData.uploadedDocuments.length > 0" class="documents-info mt-3">
              <hr>
              <p class="mb-2"><strong>Uploaded Documents:</strong></p>
              <ul class="list-unstyled">
                <li v-for="doc in submissionData.uploadedDocuments" :key="doc.name" class="small">
                  <i class="fas fa-paperclip text-muted mr-2"></i>
                  {{ doc.name }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Next Steps -->
      <div class="next-steps mb-4">
        <div class="card bg-light">
          <div class="card-body">
            <h6 class="card-title">
              <i class="fas fa-info-circle text-info mr-2"></i>
              What Happens Next?
            </h6>
            <div class="timeline">
              <div class="timeline-item active">
                <div class="timeline-marker">
                  <i class="fas fa-check"></i>
                </div>
                <div class="timeline-content">
                  <strong>Request Submitted</strong>
                  <p class="small text-muted">Your update request has been received and logged into our system.</p>
                </div>
              </div>

              <div class="timeline-item">
                <div class="timeline-marker">
                  <i class="fas fa-search"></i>
                </div>
                <div class="timeline-content">
                  <strong>Under Review</strong>
                  <p class="small text-muted">Our team will verify the information and supporting documents within 3-5 business days.</p>
                </div>
              </div>

              <div class="timeline-item">
                <div class="timeline-marker">
                  <i class="fas fa-phone"></i>
                </div>
                <div class="timeline-content">
                  <strong>Contact & Verification</strong>
                  <p class="small text-muted">We may contact you for additional information or clarification if needed.</p>
                </div>
              </div>

              <div class="timeline-item">
                <div class="timeline-marker">
                  <i class="fas fa-check-double"></i>
                </div>
                <div class="timeline-content">
                  <strong>Update Complete</strong>
                  <p class="small text-muted">Once approved, your property information will be updated in our records.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Important Notes -->
      <div class="important-notes mb-4">
        <div class="alert alert-info">
          <h6 class="alert-heading">
            <i class="fas fa-exclamation-circle mr-2"></i>
            Important Notes
          </h6>
          <ul class="mb-0 small">
            <li>Keep your reference number safe for future inquiries</li>
            <li>Processing time is typically 3-5 business days</li>
            <li>You will be contacted if additional information is required</li>
            <li>Updates to ownership transfers may require additional documentation</li>
            <li>Contact our offices if you don't hear back within 7 business days</li>
          </ul>
        </div>
      </div>

      <!-- Contact Information -->
      <div class="contact-info mb-4">
        <div class="card border-primary">
          <div class="card-body">
            <h6 class="card-title text-primary">
              <i class="fas fa-phone mr-2"></i>
              Need Help or Have Questions?
            </h6>
            <div class="row">
              <div class="col-md-6">
                <p class="small mb-1"><strong>Phone:</strong> 0700-000-000</p>
                <p class="small mb-1"><strong>Email:</strong> landrates@county.go.ke</p>
              </div>
              <div class="col-md-6">
                <p class="small mb-1"><strong>Office Hours:</strong> Mon-Fri, 8:00 AM - 5:00 PM</p>
                <p class="small mb-1"><strong>Reference Number:</strong> {{ submissionData.referenceNumber }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <div class="row">
          <div class="col-md-4">
            <button
                type="button"
                class="btn btn-outline-primary btn-block"
                @click="printReference"
            >
              <i class="fas fa-print mr-2"></i>
              Print Reference
            </button>
          </div>
          <div class="col-md-4">
            <button
                type="button"
                class="btn btn-outline-info btn-block"
                @click="checkStatus"
            >
              <i class="fas fa-search mr-2"></i>
              Check Status
            </button>
          </div>
          <div class="col-md-4">
            <button
                type="button"
                class="btn btn-success btn-block"
                @click="$emit('start-new-request')"
            >
              <i class="fas fa-plus mr-2"></i>
              New Request
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'SubmissionSuccess',
  props: {
    submissionData: {
      type: Object,
      required: true
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    printReference() {
      // Create a printable version of the reference information
      const printContent = `
        Property Update Request - Reference Information

        Reference Number: ${this.submissionData.referenceNumber}
        Property LR Number: ${this.submissionData.lrNumber}
        Submission Date: ${this.formatDate(this.submissionData.submittedAt)}
        Status: ${this.submissionData.status.toUpperCase()}

        For inquiries, contact:
        Phone: 0700-000-000
        Email: landrates@county.go.ke

        Please keep this reference number for your records.
      `

      // Open print dialog
      const printWindow = window.open('', '_blank')
      printWindow.document.write(`
        <html>
          <head>
            <title>Property Update Reference - ${this.submissionData.referenceNumber}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
              h1 { color: #2c3e50; }
              .reference { background: #f8f9fa; padding: 15px; border-left: 4px solid #007bff; }
            </style>
          </head>
          <body>
            <h1>Property Update Request</h1>
            <div class="reference">
              <pre>${printContent}</pre>
            </div>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.print()
    },

    checkStatus() {
      // This would typically open a status checking interface
      // For now, we'll show an alert with the reference number
      alert(`To check your request status, use reference number: ${this.submissionData.referenceNumber}\n\nYou can call 0700-000-000 or visit our offices.`)
    }
  }
}
</script>

<style scoped>
.submission-success {
  max-width: 800px;
  margin: 0 auto;
}

.success-icon-container {
  margin: 20px 0;
}

.success-icon {
  display: inline-block;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, #28a745, #20c997);
  border-radius: 50%;
  color: white;
  font-size: 40px;
  line-height: 80px;
  box-shadow: 0 10px 30px rgba(40, 167, 69, 0.3);
}

.success-message h3 {
  color: #28a745;
  font-weight: 600;
  margin-bottom: 10px;
}

.reference-info .card-header {
  background: linear-gradient(45deg, #28a745, #20c997);
}

.update-type-item {
  padding: 8px 0;
  font-weight: 500;
  color: #495057;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #dee2e6;
}

.timeline-item {
  position: relative;
  margin-bottom: 25px;
}

.timeline-marker {
  position: absolute;
  left: -22px;
  top: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #dee2e6;
  color: white;
  font-size: 12px;
  line-height: 30px;
  text-align: center;
  z-index: 2;
}

.timeline-item.active .timeline-marker {
  background: #28a745;
}

.timeline-content strong {
  color: #495057;
  font-weight: 600;
}

.timeline-content p {
  margin-bottom: 0;
  margin-top: 5px;
}

.card {
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  border: 1px solid #e9ecef;
}

.card-header {
  border-radius: 10px 10px 0 0;
  font-weight: 600;
}

.badge {
  font-size: 12px;
  padding: 5px 10px;
}

.btn {
  border-radius: 6px;
  font-weight: 600;
  padding: 12px 20px;
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn-success {
  background: linear-gradient(45deg, #28a745, #20c997);
  border: none;
}

.btn-success:hover {
  box-shadow: 0 5px 15px rgba(40,167,69,0.4);
}

.btn-outline-primary:hover {
  box-shadow: 0 5px 15px rgba(0,123,255,0.3);
}

.btn-outline-info:hover {
  box-shadow: 0 5px 15px rgba(23,162,184,0.3);
}

.alert {
  border-radius: 8px;
}

.alert-info {
  border-left: 4px solid #17a2b8;
}

.alert-heading {
  color: #495057;
  font-weight: 600;
}

/* Animation */
.bounceIn {
  animation: bounceIn 1s ease;
}

@keyframes bounceIn {
  0%, 20%, 40%, 60%, 80% {
    animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);
  }
  0% {
    opacity: 0;
    transform: scale3d(.3, .3, .3);
  }
  20% {
    transform: scale3d(1.1, 1.1, 1.1);
  }
  40% {
    transform: scale3d(.9, .9, .9);
  }
  60% {
    opacity: 1;
    transform: scale3d(1.03, 1.03, 1.03);
  }
  80% {
    transform: scale3d(.97, .97, .97);
  }
  100% {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .success-icon {
    width: 60px;
    height: 60px;
    font-size: 30px;
    line-height: 60px;
  }

  .timeline {
    padding-left: 25px;
  }

  .timeline-marker {
    left: -17px;
    width: 25px;
    height: 25px;
    line-height: 25px;
    font-size: 10px;
  }

  .action-buttons .col-md-4 {
    margin-bottom: 10px;
  }
}
</style>